import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { animationTransition } from '@shared/utils/animationTransition';
import { mergeRefs } from '@shared/utils/hooks/setRef';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import type { SwipeEventData } from 'react-swipeable';
import { useSwipeable } from 'react-swipeable';
import { useDefaultMediaMatchContext } from '@shared/utils/media/DefaultMediaMatchProvider';

export const useCurationItems = ({
  totalNumberOfItems,
  itemSpacing,
  onArrowClick,
}: {
  totalNumberOfItems: number;
  itemSpacing: number;
  onArrowClick?: (direction: 'left' | 'right') => void;
}) => {
  const [elementsPerPage] = useResponsive(
    {
      values: {
        mobile: 1,
        xs: 2,
        sm2: 3,
        md: 4,
        lg: 5,
      },
    },
    // we show 4 items for 13' and 15' laptops
    4,
  );

  // Settings
  const [carouselSettings, setCarouselSettings] = useState<{ currentPage: number; showLeftArrow: boolean; showRightArrow: boolean }>({
    currentPage: 0,
    showLeftArrow: false,
    showRightArrow: false,
  });

  // DOM Refs
  const containerRef = useRef<HTMLDivElement | null>(null);
  const itemRef = useRef<HTMLDivElement>(null);
  const lastPageOffset = useRef<number>(0);

  const totalPageCount = useMemo(() => {
    // `currentPage` is zero-based
    return Math.max(Math.ceil(totalNumberOfItems / elementsPerPage) - 1, 0);
  }, [elementsPerPage, totalNumberOfItems]);

  /**
   * Opt to directly mutate transform property instead of passing as a prop to
   * styled-component -- this would prevent over-generation of classes
   */
  const moveContainerByPxAmount = useEventCallback((xPos: number) => {
    const container = containerRef.current;
    if (!container) {
      return;
    }
    container.style.transform = `translate3d(${xPos}px, 0, 0)`;
  });

  const getPosByPage = useCallback(
    (page: number, pageSize: number) => {
      if (page === 0) {
        // Adjust the page alignment to the first element until last page is reached
        lastPageOffset.current = 0;
        return 0;
      }

      const lastPage = Math.floor(totalNumberOfItems / elementsPerPage);
      const offsetItems = totalNumberOfItems % elementsPerPage;
      // If it's the last page calculate lastPageOffset based on items width
      if (page === lastPage && offsetItems > 0 && itemRef.current) {
        const itemWidth = itemRef.current.clientWidth;
        const offsetItemsWidth = offsetItems * itemWidth + (offsetItems - 1) * itemSpacing;
        // Adjust the page alignment to the last element until first page is reached again
        lastPageOffset.current = pageSize - offsetItemsWidth;
      }

      const fullPageOffset = page * (pageSize + itemSpacing);
      return -(fullPageOffset - lastPageOffset.current);
    },
    [elementsPerPage, totalNumberOfItems, itemSpacing, lastPageOffset],
  );

  const repositionContainer = useCallback(
    (targetPage?: number) => {
      if (!containerRef.current) return;
      const fullContainerWidth = containerRef.current.clientWidth;
      // Account for container padding
      const containerContentWidth = fullContainerWidth - 88 * 2;

      // If `targetPage` is more than the limit, use the max page num
      const actualPage = Math.min(targetPage ?? carouselSettings.currentPage, totalPageCount);
      const nextPos = getPosByPage(actualPage, containerContentWidth);

      moveContainerByPxAmount(nextPos);
      setCarouselSettings(prev => {
        return {
          ...prev,
          currentPage: actualPage,
          showLeftArrow: actualPage > 0,
          showRightArrow: actualPage < totalPageCount,
        };
      });
    },
    [carouselSettings.currentPage, moveContainerByPxAmount, totalPageCount, getPosByPage],
  );

  useEffect(() => {
    // Adjust page size when page is loaded for the first time
    repositionContainer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elementsPerPage]);

  useEffect(() => {
    const handleResize = () => {
      repositionContainer();
    };
    // Adjust page size when window is resized
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [repositionContainer]);

  const handleArrowClick = (dir: 'left' | 'right') => {
    const { currentPage } = carouselSettings;
    const lastPage = (totalNumberOfItems - elementsPerPage) / elementsPerPage;
    if (
      // leading edge
      (dir === 'left' && currentPage === 0) ||
      // trailing edge
      (dir === 'right' && currentPage === lastPage)
    ) {
      return;
    }

    onArrowClick?.(dir);

    const pageDelta = dir === 'left' ? -1 : 1;
    const nextPage = currentPage + pageDelta;

    repositionContainer(nextPage);
  };

  return {
    handleArrowClick,
    containerRef,
    itemRef,
    elementsPerPage,
    showLeftArrow: carouselSettings.showLeftArrow,
    showRightArrow: carouselSettings.showRightArrow,
  };
};

export const SWIPE_TRANSITION_DURATION: number = 500;
export const SWIPE_RELEASE_DISTANCE = 100;

export const useCurationItemsInline = (productsCount: number, itemSpacing: number) => {
  const context = useDefaultMediaMatchContext();
  const defaultScreen = context?.defaultScreen;

  const [elementsPerPage] = useResponsive({ values: { mobile: 1, xs: 2 } }, defaultScreen === 'mobile' ? 1 : 2);

  const [isResetting, setIsResetting] = useState(false);
  const [stopScroll, setStopScroll] = useState(false);
  const [stopSwipe, setStopSwipe] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dragContainerRef = useRef<HTMLDivElement>(null);
  const itemRef = useRef<HTMLDivElement>(null);
  const trackerRefs = useRef<{ resetTimeoutId: number | undefined; isResetting: boolean }>({ resetTimeoutId: undefined, isResetting: false });
  const isSwiped = useRef(false);
  const shouldSwipe = productsCount > 1;
  const lastLeftPosition = useRef(0);

  const handleSwipe = useEventCallback((swipeData: SwipeEventData, containerWidth: number) => {
    const transform = swipeData.deltaX + SWIPE_RELEASE_DISTANCE * swipeData.velocity * (swipeData.dir === 'Left' ? -1 : 1);
    const left = lastLeftPosition.current + transform;
    const carouselWidth = containerWidth * (productsCount - 1) * -1;

    if (left > 0) return [transform - left, 0];
    if (left < carouselWidth) return [transform - (left - carouselWidth), carouselWidth];
    return [transform, left];
  });

  const handlers = useSwipeable({
    onSwipeStart: e => {
      e.event.stopPropagation();

      if (e.dir === 'Left' || e.dir === 'Right') {
        setStopScroll(true);
      } else {
        setStopSwipe(true);
      }
    },
    onSwiping: data => {
      if (stopSwipe) return;
      isSwiped.current = true;
      const dragContainer = dragContainerRef.current;
      if (dragContainer && !isResetting) {
        dragContainer.style.willChange = 'transform';
        dragContainer.style.transform = `translateX(${data.deltaX}px)`;
        dragContainer.style.transition = `none`;
      }
    },
    onSwiped: data => {
      if (stopSwipe) {
        setStopSwipe(false);
        return;
      }

      setStopScroll(false);
      const dragContainer = dragContainerRef.current;
      const wrapper = wrapperRef.current;
      if (dragContainer && wrapper && !isResetting) {
        setIsResetting(true);

        const containerWidth = itemRef.current?.clientWidth ? itemRef.current?.clientWidth + itemSpacing : 0;
        const [transform, left] = handleSwipe(data, containerWidth);

        // Snap to the nearest carousel product
        dragContainer.style.transition = animationTransition('transform');
        dragContainer.style.willChange = 'transform';
        dragContainer.style.transform = `translateX(${transform}px)`;

        // Delay resetting transform + left properties until after the current transform animation
        trackerRefs.current.resetTimeoutId = window.setTimeout(() => {
          dragContainer.style.transition = 'none';
          dragContainer.style.willChange = 'auto';
          dragContainer.style.transform = `translateX(0px)`;
          dragContainer.style.left = `${left}px`;
          setIsResetting(false);
          lastLeftPosition.current = left;
          // Setting timeout to same duration as the transition
        }, SWIPE_TRANSITION_DURATION);
      }
    },
    trackMouse: shouldSwipe,
    trackTouch: shouldSwipe,
  });

  return {
    wrapperRef: mergeRefs(handlers.ref, wrapperRef),
    dragContainerRef,
    elementsPerPage,
    itemRef,
    handlers,
    stopScroll,
  } as const;
};
